<template>
  <div class="contents">
    <div class="d-flex section-title-with-back">
      <img src="@/assets/images/Icon awesome-plus-circle.png" alt="" width="25" height="25" />
      <p class="title-bk">教場登録</p>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <router-link :to="{ name: 'school' }">戻る</router-link>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <a href="/pdf/form_school.pdf" target="_blank">フォームの説明を確認</a>
    </div>
    <div class="container">
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(confirmAdded)" ref="form">
          <table class="table table-form">
            <tbody>
              <ChoiceField
                v-model="corporationChoiceFieldMultiModels"
                extraRules="required_select"
                label="法人名（法人ID）"
                placeholder="選択して下さい"
                resourceType="corporation"
                modelFieldName="corporation"
                :choiceDisable="loggedInAsCorporation || loggedInAsBrand"
                :childFieldNames="['brand']"
                :formData="currentSchool"
              />
              <ChoiceField
                v-model="brandChoiceFieldMultiModels"
                extraRules="required_select"
                label="ブランド名（ブランドID）"
                placeholder="選択して下さい"
                resourceType="brand"
                modelFieldName="brand"
                :choiceDisable="loggedInAsBrand || isEmpty(currentSchool.corporation)"
                :formData="currentSchool"
              />
              <br />
              <IdInputField
                v-model="currentSchool.uid"
                extraRules="required"
                label="教場ID"
                placeholder="半角英数字で入力してください（-、_は可）"
                inputClass="col-lg-6"
                resourceType="school"
              />
              <PasswordField vid="password" v-model="currentSchool.password" extraRules="required" />
              <PasswordField
                v-model="verifyPassword"
                extraRules="required|verify_password:password"
                label="パスワード（確認）"
              />
              <br />
              <TextField v-model="currentSchool.name" extraRules="required" label="教場名" />
              <TextField
                v-model="currentSchool.nameReading"
                extraRules="required"
                label="教場名（フリガナ）"
                placeholder="全角カタカナ"
              />
              <PostalCodeField
                v-model="currentSchool.postalCode"
                @fillAddresses="fillAddresses($event)"
                extraRules="required"
                inputClass="col-lg-4"
              />
              <TextField
                v-model="currentSchool.state"
                extraRules="required"
                label="都道府県 市区町村"
                placeholder="例）京都府京都市中京区笋町"
              />
              <TextField
                v-model="currentSchool.address"
                extraRules="required"
                label="番地・建物名"
                placeholder="例）688 第15長谷ビル5F"
              />

              <TextField
                v-model="currentSchool.phone"
                extraRules="phone_number|required"
                label="電話番号"
                placeholder="例）075-708-8100"
              />
              <TextField
                v-model="currentSchool.fax"
                extraRules="phone_number"
                label="FAX番号"
                placeholder="例）075-708-8101"
              />
              <TextField
                v-model="currentSchool.email"
                extraRules="email"
                label="メールアドレス"
                placeholder="例）sample@tamaishiki.com"
              />
              <ProductField v-model="currentSchool.figuredProducts" :options="corporationFiguredProducts" />
            </tbody>
          </table>
          <div class="text-center">
            <button type="submit" class="btn-confirm">確認</button>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import EventBus from '@/helpers/EventBus.js';
import TextField from '@/components/forms/TextField.vue';
import ChoiceField from '@/components/forms/ChoiceField.vue';
import IdInputField from '@/components/forms/IdInputField.vue';
import PasswordField from '@/components/forms/PasswordField.vue';
import PostalCodeField from '@/components/forms/PostalCodeField.vue';
import ProductField from '@/components/forms/ProductField.vue';
import { basicCorporationNameIdPresent, basicBrandNameIdPresent } from '@/presenters/fields';

export default {
  data() {
    return {
      verifyPassword: '',
      currentSchool: {},
    };
  },
  components: {
    ValidationObserver,
    TextField,
    ChoiceField,
    IdInputField,
    PasswordField,
    PostalCodeField,
    ProductField,
  },
  computed: {
    corporationChoiceFieldMultiModels() {
      return basicCorporationNameIdPresent(this.currentSchool.corporation);
    },
    brandChoiceFieldMultiModels() {
      return basicBrandNameIdPresent(this.currentSchool.brand);
    },
    corporationFiguredProducts() {
      if (this.currentSchool.corporation) {
        return this.currentSchool.corporation.figuredProducts;
      } else {
        return null;
      }
    },
    ...mapState(['school']),
    ...mapState(['corporation']),
    ...mapState(['brand']),
    ...mapState('form', ['prevFormData']),
    ...mapGetters('auth', ['loggedInAsCorporation', 'loggedInAsBrand']),
  },
  mounted() {
    EventBus.$on('SET_USER_PROFILE', this.setCurrentRoleHandler);
  },
  beforeDestroy() {
    EventBus.$off('SET_USER_PROFILE', this.setCurrentRoleHandler);
  },
  created() {
    this.currentSchool = this.createFreshSchoolObject();
  },
  methods: {
    createFreshSchoolObject() {
      if (this.school.editingSchool) {
        // Reset content of pulldown when reselecting corporation.
        this.school.editingSchool.figuredProducts = [];
        this.$logger.debug('Editing old School with ID ' + this.school.editingSchool);
        return this.school.editingSchool;
      } else if (this.prevFormData) {
        // Reset content of pulldown when reselecting corporation.
        this.prevFormData.figuredProducts = [];
        this.$logger.debug('Restoring previous form data', this.prevFormData);
        return this.prevFormData;
      } else {
        let newSchool = {
          corporation: null,
          brand: null,
          uid: '',
          name: '',
          nameReading: '',
          postalCode: '',
          state: '',
          address: '',
          phone: '',
          email: null,
          numOfVideos: 0,
          numOfClasses: 0,
          password: '',
          figuredProducts: [],
        };
        if (this.loggedInAsCorporation) {
          newSchool['corporation'] = this.corporation.corporation;
        }
        if (this.loggedInAsBrand) {
          newSchool['brand'] = this.brand.brand;
          newSchool['corporation'] = this.brand.brand.corporation;
        }
        return newSchool;
      }
    },
    confirmAdded() {
      this.$logger.debug('Started Adding: ' + JSON.stringify(this.currentSchool));
      this.startEditingSchool(this.currentSchool);
      this.$router.push({ name: 'school-confirm' });
    },
    fillAddresses(addressComponents) {
      this.$logger.debug('addressComponents', addressComponents);
      this.currentSchool.state = addressComponents.city_state;
      this.currentSchool.address = addressComponents.address;
    },
    setCurrentRoleHandler(payload) {
      if (this.loggedInAsCorporation) {
        this.currentSchool.corporation = payload.data;
      }

      if (this.loggedInAsBrand) {
        this.currentSchool.brand = payload.data;
        this.currentSchool.corporation = payload.data.corporation;
      }
    },
    ...mapActions('school', ['startEditingSchool']),
  },
};
</script>
<style scoped>
.btn-confirm {
  margin-left: 0px;
}
.img-inline {
  margin-left: 5px;
}
.line-break {
  width: 100%;
}
.body-td-explain {
  margin-left: 5px;
  font-size: 10px;
}
</style>
